export const initialTree = [
  {
    id: "core-1",
    name: "Creative",
    description:
      "Visual storytelling, UI/UX, and production-ready design — from webtoon illustration to Figma prototypes for web3 products. I bridge aesthetic identity with interfaces that ship.",
    branches: [
      {
        id: "arts-1",
        name: "Illustration",
        description:
          "Character art, environments, and narrative panels for webtoons, VTubers, branding, and merchandise — with a focus on clarity, style cohesion, and emotional read.",
        leaves: [
          {
            id: "arts-1-1",
            name: "Concept Art",
            description:
              "Outfits, expressions, and silhouettes for worldbuilding and brand identity — supporting IP development and serialized storytelling.",
          },
          {
            id: "arts-1-2",
            name: "Environment Art",
            description:
              "Layered backgrounds and mood compositions for stories and games — lighting and depth tuned for tone and readability.",
          },
          {
            id: "arts-1-3",
            name: "Narrative Illustration",
            description:
              "Key moments and sequential panels for episodic or game content — art that guides story flow and character emotion.",
          },
        ],
      },
      {
        id: "arts-2",
        name: "UI/UX Design",
        description:
          "Clear, stylized interfaces and product flows — Figma wireframes through high-fidelity prototypes, including greenfield web3 product concepts.",
        leaves: [
          {
            id: "arts-2-1",
            name: "Prototyping",
            description:
              "Low- to high-fidelity mockups and interactive Figma prototypes that align teams on flows before build — wireframing and prototyping courses completed on the job.",
          },
          {
            id: "arts-2-2",
            name: "Feedback Cycle",
            description:
              "Iterative refinement from usability input, QA, and cross-functional review — improving accessibility and interaction clarity each pass.",
          },
          {
            id: "arts-2-3",
            name: "Interface Illustration",
            description:
              "HUDs, icons, and menu visuals with cohesive style and readable layout — including in-app screen design while implementing front-end features.",
          },
        ],
      },
      {
        id: "arts-3",
        name: "Asset Creation",
        description:
          "Game-ready 2D assets — sprites, effects, and modular UI packs built for clarity, reuse, and export compatibility.",
        leaves: [
          {
            id: "arts-3-1",
            name: "Pixel Art",
            description:
              "Animated sprites, tilesets, and retro-style interfaces — including this RPG portfolio’s pixel UI system.",
          },
          {
            id: "arts-3-2",
            name: "Background Art",
            description:
              "Layered scenes with parallax and lighting depth for storytelling or gameplay environments.",
          },
          {
            id: "arts-3-3",
            name: "Game Assets",
            description:
              "Icons, effects, and UI packs aligned to a project’s visual system — optimized for readability and seamless integration.",
          },
        ],
      },
    ],
  },
  {
    id: "core-2",
    name: "Engineering",
    description:
      "Production-grade web and web3 systems — React/Next.js front ends, Node services, Substrate/Polkadot integration, and blockchain-connected data layers for shipped consumer and platform products.",
    branches: [
      {
        id: "tech-1",
        name: "Product Engineering",
        description:
          "End-to-end product surfaces: responsive UI, server routes, internationalization, and cross-team delivery on blockchain-integrated web applications.",
        leaves: [
          {
            id: "tech-1-1",
            name: "Frontend",
            description:
              "React and Next.js with chain-connected client state, modular styling, mobile polish, and performance-minded loading for high-traffic web3 product surfaces.",
          },
          {
            id: "tech-1-2",
            name: "Backend & APIs",
            description:
              "REST and server-side routes, event-driven integrations, scheduled jobs, and production APIs that support reliable user journeys under real traffic.",
          },
          {
            id: "tech-1-3",
            name: "Server-side Logic",
            description:
              "Server-side application logic, efficient real-time and batch data access, and disciplined handling of sensitive configuration in production environments.",
          },
        ],
      },
      {
        id: "tech-2",
        name: "Blockchain & Data",
        description:
          "Substrate/Polkadot ecosystem work — chain client integration, on-chain feature collaboration, and indexed data for explorer and application consumers.",
        leaves: [
          {
            id: "tech-2-1",
            name: "Substrate & Pallets",
            description:
              "Chain client integration and on-chain feature delivery alongside pallet collaborators — reinforced by Polkadot ecosystem coursework while shipping production work.",
          },
          {
            id: "tech-2-2",
            name: "Indexing & Queries",
            description:
              "Indexed chain data and query layers for applications and explorers — emphasis on throughput, consistent read paths, and smoother cold-start behavior for downstream teams.",
          },
          {
            id: "tech-2-3",
            name: "ML & Signals",
            description:
              "LSTM and ensemble models on time-series data, evaluation with precision-recall thresholds, and deployment via Python endpoints and MetaTrader 5 integration.",
          },
        ],
      },
      {
        id: "tech-3",
        name: "Reliability & Ops",
        description:
          "Automation, incident response, and operational guardrails — keeping blockchain-integrated products stable under real traffic.",
        leaves: [
          {
            id: "tech-3-1",
            name: "Webhooks & Jobs",
            description:
              "Reliable event handling, scheduled reconciliation work, and defensive integration patterns with third-party chain and payment providers.",
          },
          {
            id: "tech-3-2",
            name: "Admin & Automation",
            description:
              "Operational tooling, safe retry flows, workflow automation (n8n/Zapier), and Python/JS scripts that reduce manual toil for engineering and ops teammates.",
          },
          {
            id: "tech-3-3",
            name: "Security Engineering",
            description:
              "Security-minded delivery — reviewing sensitive flows before release, tightening auth and dependency hygiene, and refactoring with threat models in mind.",
          },
        ],
      },
    ],
  },
  {
    id: "core-3",
    name: "Craft",
    description:
      "How I deliver — cross-functional collaboration, scalable process, and continuous learning across art-led teams and web3 engineering squads.",
    branches: [
      {
        id: "meta-1",
        name: "Delivery Leadership",
        description:
          "Leading hybrid creative and engineering work — from GIGA Studio webtoon teams to Unit Network front-end, back-end, design, and QA collaboration.",
        leaves: [
          {
            id: "meta-1-1",
            name: "Team Coordination",
            description:
              "Delegation, check-ins, and quality control across illustrators and engineers — deadlines, style fidelity, and transparent dev status reporting for complex front-end work.",
          },
          {
            id: "meta-1-2",
            name: "Mentorship",
            description:
              "Onboarding juniors with study plans, guides, and feedback — scaling output without losing creative vision.",
          },
          {
            id: "meta-1-3",
            name: "Cross-stack Flexibility",
            description:
              "Moving between front-end, back-end, data layers, and design as the sprint needs — depth in implementation, breadth in product thinking.",
          },
        ],
      },
      {
        id: "meta-2",
        name: "Systems & Process",
        description:
          "Pipelines, documentation, and version control that keep multi-repo web3 delivery merge-ready.",
        leaves: [
          {
            id: "meta-2-1",
            name: "Project Pipelines",
            description:
              "Kanban, milestones, and phased rollouts — MVP-ready baselines first, then iterative releases as products mature in production.",
          },
          {
            id: "meta-2-2",
            name: "Knowledge Management",
            description:
              "Integration templates and technical documentation — reducing onboarding friction and aligning teams on shared delivery patterns.",
          },
          {
            id: "meta-2-3",
            name: "Version Control",
            description:
              "Git branching, rebases for merge readiness, and standalone high-leverage improvements (e.g. auth and dependency hygiene) shipped early for team benefit.",
          },
        ],
      },
      {
        id: "meta-3",
        name: "Growth",
        description:
          "Self-directed learning and resilience — PBA-X, Next.js, Figma courses, and recovery through structured systems.",
        leaves: [
          {
            id: "meta-3-1",
            name: "Self-Teaching",
            description:
              "Goal-driven exploration — Polkadot ecosystem onboarding in parallel with a fast first month on the Unit Network engineering team.",
          },
          {
            id: "meta-3-2",
            name: "Resilience",
            description:
              "Structured rest and rebuilding after burnout — treating setbacks as input for better process, not stop conditions.",
          },
          {
            id: "meta-3-3",
            name: "Documentation",
            description:
              "Guides, proposals, and visual explainers that reinforce skills and support teammates.",
          },
        ],
      },
    ],
  },
];
